import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ReactNode } from 'react';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        if (error && typeof error === 'object' && 'response' in error) {
          const status = (error as { response?: { status?: number } }).response?.status;
          if (status && status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
  },
  families: {
    all: ['families'] as const,
    lists: () => [...queryKeys.families.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.families.lists(), filters] as const,
    details: () => [...queryKeys.families.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.families.details(), id] as const,
  },
  children: {
    all: ['children'] as const,
    byFamily: (familyId: string) => [...queryKeys.children.all, 'family', familyId] as const,
    detail: (id: string) => [...queryKeys.children.all, 'detail', id] as const,
  },
  healthLogs: {
    all: ['healthLogs'] as const,
    byChild: (childId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.healthLogs.all, 'child', childId, filters] as const,
    stats: (childId: string, params?: Record<string, unknown>) =>
      [...queryKeys.healthLogs.all, 'stats', childId, params] as const,
  },
  documents: {
    all: ['documents'] as const,
    byFamily: (familyId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.documents.all, 'family', familyId, filters] as const,
  },
  expenses: {
    all: ['expenses'] as const,
    byFamily: (familyId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.expenses.all, 'family', familyId, filters] as const,
    report: (familyId: string, params: Record<string, unknown>) =>
      [...queryKeys.expenses.all, 'report', familyId, params] as const,
  },
  appointments: {
    all: ['appointments'] as const,
    byFamily: (familyId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.appointments.all, 'family', familyId, filters] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.notifications.all, 'list', filters] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unreadCount'] as const,
  },
} as const;

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
